import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { TableViewerOptions, TableViewerRow } from '@app/@shared/models/table-viewer';
import { TableHelper } from '@app/@shared/utils/table-helper';
import { ColDef, GridOptions, ICellRendererParams, GridApi } from 'ag-grid-community';

@Component({
    selector: 'app-table-viewer',
    templateUrl: './table-viewer.component.html',
    styleUrls: ['./table-viewer.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class TableViewerComponent implements OnInit {
  @Input() tableViewerOptions: TableViewerOptions;
  gridOptions: GridOptions;
  gridApi: GridApi;
  columns: Array<ColDef>;
  isTableSorted = false;
  isColumnFilterActive = false;
  defaultColumnsProps: Partial<ColDef> = {
    hide: false,
    resizable: true,
    width: 180,
  };

  private rows: TableViewerRow[];

  ngOnInit(): void {
    this.rows = this.tableViewerOptions.rows;
    this.columns = this.getColumnDefs(this.tableViewerOptions.columns);

    this.gridOptions = {
      rowData: this.getRowData(this.columns, this.rows),
      suppressRowClickSelection: true,
      suppressMovableColumns: true,
      suppressContextMenu: true,
      columnDefs: this.columns,
      defaultColDef: {
        sortable: true,
        filter: 'agSetColumnFilter',
        menuTabs: ['filterMenuTab'],
        comparator: (left, right) => TableHelper.stringComparator(left, right),
        cellRenderer: (params: ICellRendererParams<any, any>) => this.cellRenderer(params),
        wrapText: true,
        autoHeight: true,
      },
      onSortChanged: ({ columns }) => {
        const sortModel = columns.filter(col => col.getSort() != null);

        this.isTableSorted = sortModel.length > 0;
      },
      onFilterChanged: ({ api }) => {
        this.isColumnFilterActive = api.isColumnFilterPresent();
      },
      onGridReady: (params) => {
        this.gridApi = params.api;
      }
    }
  }

  handleClearAllFilters(): void {
    const { columns } = this.tableViewerOptions;
    this.columns = this.getColumnDefs(columns);
    this.gridApi.setGridOption('columnDefs', this.columns)
    this.gridApi.setFilterModel(null)
  }

  handleResetSort(): void {
    this.gridApi.resetColumnState();
  }

  handleHideColumnToggle(column: ColDef): void {
    const columnToggled = { ...column, hide: !column.hide };
    this.columns = this.columns.map(c => c.colId === column.colId ? columnToggled : c)
    this.gridApi.setColumnsVisible([columnToggled.colId], !columnToggled.hide);
  }

  handleSearchValueChange(keyword: string): void {
    const filteredRowData = this.filterListByAnyKeyValue(this.gridOptions.rowData, keyword);
    this.gridApi.setGridOption('rowData', filteredRowData);
  }

  private getColumnDefs(columns: Array<ColDef>): Array<ColDef> {
    return columns.map(column => ({ ...this.defaultColumnsProps, ...column }));
  }

  /**
   * Returns an entity array
   * The entity model is formed by column names. And the value is taken from TableViewerRow
   */
  private getRowData(columns: Array<ColDef>, rows: Array<TableViewerRow>): Array<{[key: string]: string}> {
    return rows.map((tableViewerRow, index) => {
      const row = {};
      /* eslint-disable @typescript-eslint/dot-notation */
      row['originalIndex'] = index.toString(); // value not mapped to a column thus not visible
      /* eslint-enable @typescript-eslint/dot-notation */
      columns.forEach(({headerName}) => {
        row[headerName] = tableViewerRow[headerName].value;
      });
      return row;
    });
  }

  /**
   * Renders a cell in the agGrid table. If the cell contains a hyperlink, then inserts an <a> tag
   * Used by default to render every cell in the agGrid table
   */
  private cellRenderer(params: ICellRendererParams) {
    const tableViewerRows = this.rows[params.data.originalIndex]
    const { hyperlink } = tableViewerRows[params.colDef.headerName];
    if (hyperlink) {
      const legacyReportUrlWebApi = new URL(hyperlink);
      const uuiReportUrl = hyperlink.replace(legacyReportUrlWebApi.origin, window.location.origin);
      return `<a href="${uuiReportUrl}" target="_blank">${params.value}</a>`;
    } else {
      return params.value;
    }
  }

  private filterListByAnyKeyValue<T>(array: Array<T>, keyword: string): Array<T> {
    return array.filter(o =>
      Object.keys(o).some(k => o[k].toLowerCase().includes(keyword.toLowerCase())));
  }
}
